import React, { useCallback, useEffect, useState } from 'react';

import { Group, Title, Text, useMantineTheme } from '@mantine/core';
import { useDebouncedValue, useDisclosure } from '@mantine/hooks';
import { IconEdit, IconTrashX } from '@tabler/icons-react';
import Menu from 'components/Menu';
import Table from 'components/Table';
import useClassTypes from 'hooks/classType/useClassTypes';
import { CellProps } from 'react-table';
import { KiloClassType } from 'types';

import DrawerClassTypeForm from './DrawerClassTypeForm';
import ModalClassTypeDelete from './ModalClassTypeDelete';

interface IClassTypeTableContainerProps {
  permission?: boolean;
}

const ClassTypeTableContainer = ({ permission = true }: IClassTypeTableContainerProps): JSX.Element => {
  const theme = useMantineTheme();
  const [ordering, setOrdering] = useState('name');
  const [ascending, setAscending] = useState(true);

  const { data, fetchNextPage, hasNextPage, isSuccess, isError, isLoading, isFetchingNextPage } =
    useClassTypes({
      ordering: ascending ? ordering : `-${ordering}`,
    });

  const [selected, setSelected] = useState<KiloClassType | undefined>();
  const [tableData, setTableData] = useState<KiloClassType[]>([]);

  const [isEditFormOpened, { open: openEditForm, close: closeEditForm }] = useDisclosure(false);
  const [debouncedIsEditFormOpened] = useDebouncedValue(isEditFormOpened, 0);
  const [deleteModalOpened, { open: openDeleteModal, close: closeDeleteModal }] = useDisclosure(false);

  const handleHeaderClick = useCallback(
    (header: string): void => {
      setOrdering(header);
      setAscending(!ascending);
    },
    [ascending],
  );

  const handleEdit = (classType: KiloClassType): void => {
    setSelected(classType);
    openEditForm();
  };

  const handleCloseEdit = (): void => {
    setSelected(undefined);
    closeEditForm();
  };

  const handleDelete = (classType: KiloClassType): void => {
    setSelected(classType);
    openDeleteModal();
  };

  const columns = [
    {
      Header: 'Class Name',
      accessor: 'name' as const,
      Cell: ({ row }: CellProps<KiloClassType>) => (
        <Text
          onClick={() => handleEdit(row.original)}
          weight='bold'
          underline
          sx={{ cursor: 'pointer', width: 'fit-content' }}
        >
          {row.original.name}
        </Text>
      ),
    },
    {
      Header: 'Duration',
      accessor: 'duration' as const,
      Cell: ({ row }: CellProps<KiloClassType>) =>
        `${row.original.duration} ${row.original.duration === 1 ? 'min' : 'mins'}`,
    },
    {
      id: 'action',
      Cell: ({ row }: CellProps<KiloClassType>) => (
        <Group position='right' px='xs'>
          <Menu
            position='bottom-end'
            disabled={!permission}
            items={[
              {
                title: 'Edit',
                onClick: () => handleEdit(row.original),
                icon: <IconEdit size={theme.fontSizes.sm} />,
              },
              {
                title: 'Delete',
                onClick: () => handleDelete(row.original),
                icon: <IconTrashX size={theme.fontSizes.sm} />,
                color: 'red',
              },
            ]}
          />
        </Group>
      ),
      minWidth: '50px',
      maxWidth: '50px',
      sticky: true,
    },
  ];

  useEffect(() => {
    if (!isLoading && data) {
      setTableData(data.pages.map(page => page.data.results).flat());
    }
  }, [isLoading, data]);

  return (
    <>
      <Table
        tableData={tableData}
        columns={columns}
        isSuccess={isSuccess}
        isLoading={isLoading}
        isError={isError}
        isFetchingNextPage={isFetchingNextPage}
        hasNextPage={!!hasNextPage}
        fetchNextPage={fetchNextPage}
        clickableHeaders={['name', 'duration']}
        handleHeaderClick={handleHeaderClick}
        ordering={ordering}
        ascending={ascending}
      />

      <DrawerClassTypeForm
        formId='edit-kilo-class-type-form'
        title={<Title order={3}>{permission ? 'Edit' : 'View'} Class Type</Title>}
        kiloClassTypeId={selected?.kiloClassTypeId}
        opened={debouncedIsEditFormOpened}
        closeHandler={handleCloseEdit}
        type='edit'
        permission={permission}
      />

      {selected && (
        <ModalClassTypeDelete
          opened={!!selected && deleteModalOpened}
          handleClose={closeDeleteModal}
          kiloClassType={selected}
        />
      )}
    </>
  );
};

export default ClassTypeTableContainer;
